import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { scheduleKeys } from "./queryKeys";
import { fetchCompanyShifts } from "@/services/schedule/shiftService";
import { useCompanyTimezone } from "@/hooks/useCompanyTimezone";
import { getCompanyWeekRange } from "@/lib/scheduleTime";

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
}

export const useCompanyShifts = (
  companyId: string | undefined, 
  currentWeek: Date,
  employees: Profile[]
) => {
  const tz = useCompanyTimezone();
  // Build the fetch window in the COMPANY's timezone so the query range
  // matches the days the user actually sees rendered on the grid.
  const { startISO: weekStartISO, endISO: weekEndISO } = useMemo(
    () => getCompanyWeekRange(currentWeek, tz),
    [currentWeek, tz]
  );
  
  // Memoized profile map for enrichment
  const profilesMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of employees) {
      const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
      m.set(p.id, name || '—');
    }
    return m;
  }, [employees]);

  // Include employees.length in query key to re-run enrichment when employees load
  const employeesLoaded = employees.length > 0;

  return useQuery({
    // Include employeesLoaded to re-run queryFn once employees are available
    queryKey: [...scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO), employeesLoaded],
    queryFn: async () => {
      if (!companyId) throw new Error('Company ID required');
      const data = await fetchCompanyShifts(companyId, weekStartISO, weekEndISO);
      
      // CRITICAL: Maintain exact same data enrichment
      return data.map(shift => ({
        ...shift,
        status: (shift.status as 'active' | 'open') || 'active',
        employee_name: shift.status === 'open' || shift.profile_id === null 
          ? 'Open shift' 
          : profilesMap.get(shift.profile_id) ?? '—'
      }));
    },
    enabled: !!companyId,
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter for real-time collaboration)
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: 30 * 60 * 1000, // 30 minutes - reduced from 30 seconds to save costs
  });
};
